export interface OTPResponse {
  loginType: string;
  authType: string;
  status: string;
  user: UserPatient;
}

export interface UserPatient {
  id?: number;
  name: string;
  avatar: string | null;
  profile_id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  hash: string;
  role: string;
  details: any | null;
  address: Address;
  created_at: string;
  updated_at: string;
  phone_country_code: string;
  photo_uuid: string;
  insurance: any | null;
  condition: {
    height?: number;
    weight?: number;
    gender?: string;
    blood_type?: string;
    existing_conditions?: string[];
    allergies?: string[];
  } | null;
  clinician: any | null;
  date_of_birth: string | null;
  quickblox_password: null;
  quickblox_id: null;
  utc_offset: number | null;
  time_zone: string | null;
  status: string;
  user_name: string | null;
  note: string | null;
  patient_id: any | null;
  settings: Settings;
  login_source: string | null;
  google_account: any | null;
  facebook_account: any | null;
  apple_account: any | null;
  linked_accounts: Record<string, any>;
  hasSetBiometrics: boolean;
  hasCompletedOnboarding: boolean;
  gender: string | null;
  existing_conditions: string[] | null;
  allergies: string[] | null;
}

interface Settings {
  mu: string;
}

interface Address {
  country: string;
  state: string;
  city: string;
  zip: string;
  address_line_1: string;
  address_line_2: string;
}

export enum DeviceType {
  THERAPY = "THERAPY",
  PULSE_OXIMETER = "PULSE_OXIMETER",
  UNKNOWN = "UNKNOWN",
}

export interface PLXMeasurement {
  spO2: number | null; // Oxygen saturation percentage
  pulseRate: number | null; // Pulse rate in BPM
  timestamp?: Date;
  measurementStatus?: number;
  deviceStatus?: number;
  pulseAmplitudeIndex?: number | null;
  lastReading?: Date;
}

export enum TherapyPhase {
  PAUSE = "pause",
  INSPIRATORY = "inspiratory",
  EXPIRATORY = "expiratory",
  INACTIVE = "inactive",
}

// Interface for therapy session parameters
export interface TherapyParameters {
  pausePressure: number;
  inspPressure: number;
  expPressure: number;
  pauseDuration: number; // in milliseconds
  inspDuration: number; // in milliseconds
  expDuration: number; // in milliseconds
  cycleDuration: number; // total cycle time in milliseconds
  enabledPhases: TherapyPhase[]; // Which phases are active in this session
  currentCycleNumber: number;
  totalCycleCount: number;
  oscStatus: number;
  inspTriggerStatus: number;
  triggerTextPosition?: { x: number; y: number };
  coughCueTextPosition?: { x: number; y: number };
}
export interface AdherenceData {
  stats: {
    therapyDays: number;
    missedDays: number;
    avgAdherence: number;
  };
  sessions: Array<{
    date: string;
    timeSlots: {
      "12AM": number;
      "6AM": number;
      "12PM": number;
      "6PM": number;
    };
    cycleCount: number;
  }>;
}
export interface CycleData {
  id: number;
  pause: number; // 0 to 15
  inspiration: number; // 0 to 70
  expiration: number; // 0 to -70
  feedback: "preferred" | "disliked" | "none";
}

export interface PressureSetting {
  label: string;
  pause: number;
  insp: number;
  exp: number;
}

export interface TitrationData {
  therapyDate: string;
  cycles: CycleData[];
  pressureSettings: PressureSetting[];
}

export interface EnhancedTitrationCardProps {
  data: TitrationData | null;
  isLoading: boolean;
  onSync: () => void;
}

export interface TherapySession {
  date: string;
  time: string;
  refId: string;
  count: number;
  locallyAvailable: boolean;
}

export interface TitrationSession {
  date: string;
  time: string;
  refId: string;
  locallyAvailable: boolean;
}

export interface StoredTitrationSession {
  session: TitrationSession;
  deviceId: string;
  titrationData: TitrationData;
  fetchedAt: string;
}

export interface StoredTitrationSessionsList {
  date: string;
  time: string;
  refId: string;
  locallyAvailable: boolean;
  deviceId: string;
}

export interface DataPoint {
  time: number;
  value: number;
}

export interface CurveAnalysisData {
  flowData: DataPoint[];
  pressureData: DataPoint[];
  volumeData: DataPoint[];
}

export interface GraphType {
  type: "flow" | "pressure" | "volume";
  enabled: boolean;
  color: string;
  unit: string;
  range: [number, number];
  decimals: number;
  useFixedRangeInLive?: boolean;
}

export interface CurveAnalysisCardProps {
  data: CurveAnalysisData | null;
  isLoading: boolean;
  onSync: (
    session: TherapySession,
    pressure: boolean,
    flow: boolean,
    volume: boolean
  ) => Promise<void>;
  miniGraphSamplingRate?: number; // Optional, default is 1000ms
  minHandleSeparationPx?: number; // Optional, default is 10px
  enableSmoothTransitions?: boolean; // Optional, default is true
  // graphs: GraphType[];
  // selectedGraph: GraphType | null;
  // onGraphSelect: (graph: GraphType) => void;
}

/**
 * Bitmask flags for curve measurements matching C++ CurveMeasurementFlags enum
 *
 * Combine flags with bitwise OR to specify which measurements are included:
 * - PRESSURE_FLAG | FLOW_FLAG = 0x03 (pressure + flow)
 * - PRESSURE_FLAG | VOLUME_FLAG = 0x05 (pressure + volume)
 * - PRESSURE_FLAG | FLOW_FLAG | VOLUME_FLAG = 0x07 (all measurements)
 *
 * Bits 3-7 are reserved for future measurement types.
 */
export enum CurveMeasurementFlags {
  PRESSURE_FLAG = 0x01, // bit 0
  FLOW_FLAG = 0x02, // bit 1
  VOLUME_FLAG = 0x04, // bit 2
  // bits 3-7 reserved for future measurements (temperature, humidity, etc.)
}
/**
 * Parsed chunk header structure
 *
 * HEADER FORMAT (9+ bytes):
 * ┌─────────────────┬──────────────┬─────────────┬──────────────────┬─────────────────┬─────────────────┬─────────────────┬─────────────────┬─────────────────┬─────────────────┐
 * │ MessageType (1) │ Position (1) │ ChunkNum(2) │ TotalChunks (2)  │ RefIdSize (1)   │ EventIdSize (1) │ Measurements(1) │ RefId (0-8)     │ EventId (0-8)   │ Payload (var)   │
 * └─────────────────┴──────────────┴─────────────┴──────────────────┴─────────────────┴─────────────────┴─────────────────┴─────────────────┴─────────────────┴─────────────────┘
 */
export interface ChunkHeader {
  messageType: number;
  chunkPosition: number;
  chunkNumber: number;
  totalChunks: number;
  refIdSize: number;
  eventIdSize: number;
  includedMeasurements: number; // NEW: Bitmask for included curve measurements
  refId?: number;
  eventId?: number;
}

/**
 * Complete parsed chunk result
 */
export interface ParsedChunk {
  header: ChunkHeader;
  payload: Uint8Array;
  isValid: boolean;
  errorMessage?: string;
}

export enum MessageType {
  LOGS = 0x01,
  ADHERENCE = 0x02,
  TITRATION = 0x03,
  CURVES = 0x04,
  MAIN_MAP = 0x07,
  VAL_MAP = 0x08,
  UPDATED_TITRATION_SUMMARY = 0x05,
  UPDATED_CURVES_LIST = 0x06,
  REALTIME_CURVES = 0x09,
}

export enum ChunkPosition {
  MIDDLE = 0x00,
  START = 0x01,
  END = 0x02,
  SINGLE = 0x03,
}

export interface AdherenceData {
  stats: {
    therapyDays: number;
    missedDays: number;
    avgAdherence: number;
  };
  sessions: Array<{
    date: string;
    timeSlots: {
      "12AM": number;
      "6AM": number;
      "12PM": number;
      "6PM": number;
    };
    cycleCount: number;
  }>;
}

export interface StoredAdherenceData {
  adherenceData: AdherenceData;
  deviceId: string;
  dateRange: {
    startDate: string;
    endDate: string;
  };
  fetchedAt: string; // ISO timestamp
}

export interface StoredTitrationData {
  titrationData: TitrationData;
  deviceId: string;
  fetchedAt: string; // ISO timestamp
}

// Interface for stored curve analysis session
export interface StoredCurveSession {
  therapySession: TherapySession;
  curveAnalysisData: CurveAnalysisData;
  deviceId: string;
  fetchedAt: string; // ISO timestamp
}

// Interface for stored curve analysis data (contains multiple sessions)
export interface StoredCurveAnalysisData {
  sessions: StoredCurveSession[];
  currentDeviceId: string; // tracks which device sessions belong to
}

export interface RealtimeData {
  flowData: DataPoint[];
  pressureData: DataPoint[];
  volumeData: DataPoint[];
}

export interface RealtimeCurvePoint {
  elapsedTimeMs: number; // Elapsed time since therapy start in milliseconds
  pressure?: number; // Pressure value (original value, auto-scaled from raw)
  flow?: number; // Flow value (original value, auto-scaled from raw)
  volume?: number; // Volume value (original value, auto-scaled from raw)
}

/**
 * Enum representing BLE message types with their corresponding bit positions (0-15)
 * This ensures the final 16-bit value stays within valid range (0-65535)
 */
export enum RequestMessageType {
  // Current message types mapped to lower bit positions
  MAPS_FOR_LOGS = 0, // bit 0 → 2^0 = 1
  LOGS = 1, // bit 1 → 2^1 = 2
  ADHERENCE = 2, // bit 2 → 2^2 = 4
  TITRATION = 3, // bit 3 → 2^3 = 8
  HISTORICAL_CURVES = 4, // bit 4 → 2^4 = 16 (Curves on Poke)
  REALTIME_CURVES = 5, // bit 5 → 2^5 = 32
  MIE_DATA = 6, // bit 6 → 2^6 = 64
  MANUAL_MODE_TIMES = 7, // bit 7 → 2^7 = 128
  PRESSURE_GAUGE = 8, // bit 8 → 2^8 = 256

  // Bits 9-15 reserved for future use
  RESERVED_9 = 9, // bit 9 → 2^9 = 512
  RESERVED_10 = 10, // bit 10 → 2^10 = 1024
  RESERVED_11 = 11, // bit 11 → 2^11 = 2048
  RESERVED_12 = 12, // bit 12 → 2^12 = 4096
  RESERVED_13 = 13, // bit 13 → 2^13 = 8192
  RESERVED_14 = 14, // bit 14 → 2^14 = 16384
  RESERVED_15 = 15, // bit 15 → 2^15 = 32768
}

/**
 * Interface for the function result containing both the API string and debug info
 */
export interface RequestMessageTypeResult {
  apiString: string;
  debugInfo: {
    requestedTypes: string[];
    validTypes: string[];
    invalidTypes: string[];
    binaryRepresentation: string;
    decimalValue: number;
    bitPositions: number[];
  };
}

export interface TherapyParamsPart1 {
  enabledPhases: TherapyPhase[];
  pauseDuration: number;
  inspDuration: number;
  expDuration: number;
  inspTriggerStatus: number;
  currentCycleNumber: number;
}

export interface TherapyParamsPart2 {
  pausePressure: number;
  inspPressure: number;
  expPressure: number;
  oscStatus: number;
  totalCycles: number;
}
export interface BleError {
  errorMessage?: string;
  message?: string;
}
export interface UserProfileRaw {
  created_at: string;
  updated_at: string;
  id: number;
  username: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  password: string;
  role: string;
  details: Record<string, any>;
  address: Record<string, any>;
  patient_id: Record<string, any>;
  phone_country_code: string;
  photo_uuid: string;
  insurance: Record<string, any>;
  condition: {
    gender: string;
    existing_conditions: string[];
  };
  clinician: Record<string, any>;
  dob: string;
  quickbloxPassword: string;
  quickbloxId: number;
  utc_offset: string | null;
  time_zone: string | null;
  status: string;
  note: string;
  settings: {
    mu: string;
  };
  login_source: string;
  avatar: string;
  google_account: {
    email: string;
    name: string;
    avatar: string;
    lastUpdated: string;
    firstConnected: string;
    lastLoggedIn: string;
  };
  facebook_account: {
    email: string;
    name: string;
    avatar: string;
    lastUpdated: string;
    firstConnected: string;
    lastLoggedIn: string;
  };
  apple_account: {
    email: string;
    name: string;
    lastUpdated: string;
    firstConnected: string;
    lastLoggedIn: string;
  };
  linked_accounts: {
    apple: {
      email: string;
      lastUpdated: string;
      connected: boolean;
    };
    apple_account: {
      email: string;
      name: string;
      connected: boolean;
      lastUpdated: string;
      firstConnected: string;
      lastLoggedIn: string;
    };
    facebook_account: {
      email: string;
      name: string;
      avatar: string;
      connected: boolean;
      lastUpdated: string;
      firstConnected: string;
      lastLoggedIn: string;
    };
    google_account: {
      email: string;
      name: string;
      avatar: string;
      connected: boolean;
      lastUpdated: string;
      firstConnected: string;
      lastLoggedIn: string;
    };
  };
}
declare global {
  interface Window {
    therapyRef: React.RefObject<HTMLDivElement>;
  }
}
